import { useEffect, useRef, useState } from "react";
import api from "./api";
import { history } from "./history";
import { setCustomFields } from "./aiReview";

// User-defined AI columns (RAL, Gloss, Box size ...), shared by all users so AI looks for them in every new file.
export function useAiCustomFields() {
    const [fields, setFields] = useState([]);
    const ref = useRef([]);
    const apply = (list) => { ref.current = list; setCustomFields(list); setFields(list); };
    useEffect(() => { api.get("/ai/custom-fields").then((r) => apply(r.data || [])).catch(() => {}); }, []);
    const create = async (label) => {
        const { data } = await api.post("/ai/custom-fields", { label });
        if (!ref.current.some((f) => f.key === data.key)) apply([...ref.current, data]);
        return data;
    };
    const rename = async (key, label) => {
        const { data } = await api.put(`/ai/custom-fields/${key}`, { label });
        apply(ref.current.map((f) => (f.key === key ? data : f)));
    };
    const remove = async (key) => {
        await api.delete(`/ai/custom-fields/${key}`);
        apply(ref.current.filter((f) => f.key !== key));
    };
    return { fields, create, rename, remove };
}

// Saves a marked example (value + surrounding text + place) for learning; Ctrl+Z removes it again.
export function addCustomExample(key, body) {
    const post = () => api.post(`/ai/custom-fields/${key}/examples`, body).then((r) => r.data.id).catch(() => null);
    let idP = post();
    history.attach({
        label: "AI",
        undo: async () => { const id = await idP; if (id) await api.delete(`/ai/custom-fields/${key}/examples/${id}`).catch(() => {}); },
        redo: () => { idP = post(); return idP; },
    });
}

const center = (b) => [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
// PDF text around a marked box (the "context" AI uses to find the same kind of value elsewhere).
export const nearText = (textItems, box, r = 0.08) => {
    if (!box) return [];
    const [cx, cy] = center(box);
    return (textItems || []).map((ti) => { const [x, y] = center(ti.box); return { s: ti.s, d: Math.hypot(x - cx, y - cy), inside: x >= box[0] && x <= box[2] && y >= box[1] && y <= box[3] }; })
        .filter((x) => !x.inside && x.d < r).sort((a, b) => a.d - b.d).slice(0, 10).map((x) => x.s.slice(0, 60));
};

// After OCR of a marked area: exact value candidates ("Product width (mm): 3175" -> "3175", then the full text).
export function valueCandidates(text) {
    const s = String(text || "").trim();
    if (!s) return [];
    const nums = [...s.matchAll(/\d+(?:[.,]\d+)?/g)].map((m) => m[0]);
    return [...new Set([...(nums.length && nums[0] !== s ? nums : []), s])].slice(0, 6);
}

// Field hinted by the label inside the OCR text (width / height / quantity).
const NUM_KEYS = ["width", "height", "qty"];
// Numeric fields get the bare number ("3175"), all others the full read text ("Munkedamsveien 62C").
export const pickValue = (key, cands) => (!cands?.length ? "" : NUM_KEYS.includes(key) ? cands[0] : cands[cands.length - 1]);
export function guessField(text) {
    const s = String(text || "").toLowerCase();
    if (/width|bredde|plotis|breite|\bb\s*[:=]/.test(s)) return "width";
    if (/height|høyde|hoyde|aukštis|aukstis|höhe|\bh\s*[:=]/.test(s)) return "height";
    if (/\bqty\b|antall|kiekis|\bstk\b|\bvnt\b/.test(s)) return "qty";
    return null;
}

export const nm = (s) => String(s ?? "").replace(/[\s\-_.:/]/g, "").toLowerCase();
// Fills custom columns from a whole-document scan; values typed / marked by the user are never overwritten.
export function mergeScan(items, found, keys) {
    let filled = 0;
    const out = items.map((it) => {
        const n = nm(it.f.name?.value);
        if (!n) return it;
        const pages = it.pages?.length ? it.pages : [it.page];
        const hits = found.filter((x) => nm(x.name?.value) === n).sort((a, b) => pages.includes(b.page) - pages.includes(a.page));
        if (!hits.length) return it;
        const f = { ...it.f };
        keys.forEach((k) => {
            if (f[k]?.edited || f[k]?.manual) return;
            const h = hits.find((x) => String(x[k]?.value ?? "").trim());
            if (!h) return;
            f[k] = { value: h[k].value, box: h[k].box, conf: h[k].conf ?? 0.8, orig: h[k].value, page: h.page !== it.page ? h.page : undefined };
            filled += 1;
        });
        return { ...it, f };
    });
    return { items: out, filled };
}

export const knownExamples = (items, keys) => items.flatMap((it) => keys
    .filter((k) => String(it.f[k]?.value ?? "").trim() && (it.f[k].edited || it.f[k].manual))
    .map((k) => ({ pos: String(it.f.name?.value ?? ""), key: k, value: String(it.f[k].value) }))).slice(0, 40);
